import notification from "antd/es/notification";
import { defineMessages } from "react-intl";

import TourDescriptor from "@app/component/flows/types/TourDescriptor";

const messages = defineMessages({
  step01: {
    id: "guides.resources.addingResourcesTour.step01",
    defaultMessage: "Where to Find Pinned Resources",
  },
  step01Description: {
    id: "guides.resources.addingResourcesTour.step01.description",
    defaultMessage:
      "Pinned Resources live in the Resources section of MMC. Go to the Resources tab.",
  },
  step02: {
    id: "guides.resources.addingResourcesTour.step02",
    defaultMessage: "Adding Resources",
  },
  step02Description: {
    id: "guides.resources.addingResourcesTour.step02.description",
    defaultMessage: "Click Add Resource",
  },
  step03: {
    id: "guides.resources.addingResourcesTour.step03",
    defaultMessage: "Adding Resources",
  },
  step03Description: {
    id: "guides.resources.addingResourcesTour.step03.description",
    defaultMessage:
      "You can choose whether you’re adding a File or a Link. For now let's proceed with a Link.",
  },
  step04: {
    id: "guides.resources.addingResourcesTour.step04",
    defaultMessage: "Adding a Link",
  },
  step04Description: {
    id: "guides.resources.addingResourcesTour.step04.description",
    defaultMessage: "Please enter the Name and URL, and click Next to proceed.",
  },
  step05: {
    id: "guides.resources.addingResourcesTour.step05",
    defaultMessage: "Adding a Link",
  },
  step05Description: {
    id: "guides.resources.addingResourcesTour.step05.description",
    defaultMessage:
      "Select the Teams or Users to share the resource with and click share. If you do not wish to share the resource, select Do not share.",
  },
  step06: {
    id: "guides.resources.addingResourcesTour.step06",
    defaultMessage: "Uploading a File",
  },
  step06Description: {
    id: "guides.resources.addingResourcesTour.step06.description",
    defaultMessage: "Now let's upload a file. Click Add Resource button again.",
  },
  step07: {
    id: "guides.resources.addingResourcesTour.step07",
    defaultMessage: "Uploading a File",
  },
  step07Description: {
    id: "guides.resources.addingResourcesTour.step07.description",
    defaultMessage: "Select 'Upload File'",
  },
  step08: {
    id: "guides.resources.addingResourcesTour.step08",
    defaultMessage: "Uploading a File",
  },
  step08Description: {
    id: "guides.resources.addingResourcesTour.step08.description",
    defaultMessage:
      "Upload the file by dragging and dropping into the modal or clicking upload and selecting the file from your device.",
  },
  step09: {
    id: "guides.resources.addingResourcesTour.step09",
    defaultMessage: "Uploading a File",
  },
  step09Description: {
    id: "guides.resources.addingResourcesTour.step09.description",
    defaultMessage:
      "You can also define the access for the file as:{br}🔴 Restricted - Access restricted to you and anyone this resource is shared with.{br}🟢 Anyone with link - Anyone on the internet with the link can view. Required for attaching resources to emails.",
  },
  step10: {
    id: "guides.resources.addingResourcesTour.step10",
    defaultMessage: "Uploading a File",
  },
  step10Description: {
    id: "guides.resources.addingResourcesTour.step10.description",
    defaultMessage: "Upload any file and click Next to proceed.",
  },
  step11: {
    id: "guides.resources.addingResourcesTour.step11",
    defaultMessage: "Uploading a File",
  },
  step11Description: {
    id: "guides.resources.addingResourcesTour.step11.description",
    defaultMessage:
      "Select the Teams or Users to share the resource with and click share. If you do not wish to share the resource, select Do not share.",
  },
  step12: {
    id: "guides.resources.addingResourcesTour.step12",
    defaultMessage: "Tour Complete",
  },
  step12Description: {
    id: "guides.resources.addingResourcesTour.step12.description",
    defaultMessage: "🎉 Congratulations! You've completed this tour.",
  },
});

export const addingResourcesTourConfiguration: TourDescriptor = {
  id: "mmc-resources-adding",
  steps: [
    {
      id: "intro",
      description: messages.step01Description,
      options: {
        disableInteraction: false,
        expectRouteChange: true,
        showBackground: true,
        showButtons: false,
      },
      selector: "[data-navbar-item='/resources']",
      title: messages.step01,
      waitForEvent: {
        eventType: "click",
        selector: "[data-navbar-item='/resources']",
      },
    },
    {
      id: "adding-1",
      description: messages.step02Description,
      options: {
        disableInteraction: false,
        showBackground: true,
        showButtons: false,
      },
      position: "left",
      selector: "[data-action-id=add-resource-button]",
      title: messages.step02,
      waitForEvent: {
        eventType: "click",
        selector: "[data-action-id=add-resource-button]",
      },
    },
    {
      id: "adding-2",
      description: messages.step03Description,
      options: {
        disableInteraction: false,
        showBackground: true,
        showButtons: false,
      },
      position: "left",
      selector: "[data-menu-id$=addLink]",
      title: messages.step03,
      waitForEvent: {
        eventType: "click",
        selector: "[data-menu-id$=addLink]",
      },
    },
    {
      id: "adding-link-1",
      description: messages.step04Description,
      options: {
        disableInteraction: false,
        showBackground: false,
        showButtons: false,
      },
      position: "bottom",
      selector: "[class*=AddResourcesWizard_container] .ant-btn-primary",
      title: messages.step04,
      waitForEvent: {
        eventType: "click",
        selector: "[class*=AddResourcesWizard_container] .ant-btn-primary",
      },
    },
    {
      id: "adding-link-2",
      description: messages.step05Description,
      options: {
        disableInteraction: false,
        showBackground: true,
        showButtons: false,
      },
      position: "bottom",
      selector: "[data-action-id=do-not-share]",
      title: messages.step05,
      waitForEvent: {
        eventType: "click",
        selector: "[data-action-id=do-not-share]",
      },
    },
    {
      id: "adding-file-1",
      description: messages.step06Description,
      onBefore: async () => {
        // let's try to close "link resource added successfully" notification
        // because it partially overlaps the "Add Resource" button

        let attemptsLeft = 5;
        const callback = () => {
          // close all notifications
          notification.destroy();
          if (--attemptsLeft) {
            setTimeout(callback, 1000);
          }
        };
        setTimeout(callback, 1000);
      },
      options: {
        disableInteraction: false,
        showBackground: true,
        showButtons: false,
      },
      position: "left",
      selector: "[data-action-id=add-resource-button]",
      title: messages.step06,
      waitForEvent: {
        eventType: "click",
        selector: "[data-action-id=add-resource-button]",
      },
    },
    {
      id: "adding-file-2",
      description: messages.step07Description,
      options: {
        disableInteraction: false,
        showBackground: true,
        showButtons: false,
      },
      position: "left",
      selector: "[data-menu-id$=uploadFile]",
      title: messages.step07,
      waitForEvent: {
        eventType: "click",
        selector: "[data-menu-id$=uploadFile]",
      },
    },
    {
      id: "adding-file-3",
      description: messages.step08Description,
      position: "bottom",
      selector: "[class*=AddResourcesWizard_filesUpload]",
      title: messages.step08,
    },
    {
      id: "adding-file-4",
      description: messages.step09Description,
      onAfter: async () => {
        const element = document.querySelector("[class*=AddResourcesWizard_access] button");
        if (element) {
          (element as HTMLElement).click();
        }
      },
      onBefore: async () => {
        const element = document.querySelector("[class*=AddResourcesWizard_access] button");
        if (element) {
          (element as HTMLElement).click();
        }
      },
      position: "right",
      selector: ".mmc-js-file-access-dropdown",
      title: messages.step09,
    },
    {
      id: "adding-file-5",
      description: messages.step10Description,
      options: {
        disableInteraction: false,
        showBackground: false,
        showButtons: false,
      },
      position: "right",
      selector: "[class*=AddResourcesWizard_container] .ant-btn-primary",
      title: messages.step10,
      waitForEvent: {
        eventType: "click",
        selector: "[class*=AddResourcesWizard_container] .ant-btn-primary",
      },
    },
    {
      id: "adding-file-6",
      description: messages.step11Description,
      options: {
        disableInteraction: false,
        showBackground: true,
        showButtons: false,
      },
      position: "bottom",
      selector: "[data-action-id=do-not-share]",
      title: messages.step11,
      waitForEvent: {
        eventType: "click",
        selector: "[data-action-id=do-not-share]",
      },
    },
    {
      id: "completed",
      description: messages.step12Description,
      title: messages.step12,
    },
  ],
};
